import { createRootRoute, Outlet, useMatchRoute, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import Navbar from "@/features/home/Navbar";
import ScrollToTop from "react-scroll-to-top";
import NotFound from "@/features/not-found/page";
import { useDarkMode } from "@/hooks/useDarkMode";
import { GetInTouchModal } from "@/features/get-in-touch/GetInTouchModal";
import { MixpanelCaptureDevPanel } from "@/components/MixpanelCaptureDevPanel";

const isProduction = import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

export const Route = createRootRoute({
  component: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const isDark = useDarkMode();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const matchRoute = useMatchRoute();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const router = useRouter();

    // Determine if the current route matches'/mysavedplaces'
    const isMySavedPlacesActive = matchRoute({
      to: "/savedplacedetails/$savedPlaceId/user/$userId",
    });

    // Determine if the current route matches'/'
    const isMapActive = matchRoute({ to: "/" });

    // Determine if the current route is venue photos page
    const isVenuePhotosActive = matchRoute({ to: "/venuephotos/$venueSlug" });

    // Lock document scroll when root uses fixed-height layout (index/saved places)
    // so only the inner <main> scrolls and we avoid double scrollbars
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const useInnerScrollLayout = isMySavedPlacesActive || isMapActive;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (useInnerScrollLayout) {
        document.body.classList.add("app-inner-scroll");
      } else {
        document.body.classList.remove("app-inner-scroll");
      }
      return () => {
        document.body.classList.remove("app-inner-scroll");
      };
    }, [useInnerScrollLayout]);

    // Route change focus management - move focus to main content on navigation
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const handleRouteChange = () => {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const mainElement = document.getElementById("main");
          if (mainElement) {
            // Set tabindex to allow focus, then focus
            mainElement.setAttribute("tabindex", "-1");
            mainElement.focus();
            // Remove tabindex after focus to prevent it from being in tab order
            mainElement.addEventListener("blur", () => {
              mainElement.removeAttribute("tabindex");
            }, { once: true });
          } else {
            // Fallback: if #main doesn't exist, try to focus first h1
            const firstHeading = document.querySelector("h1");
            if (firstHeading) {
              const heading = firstHeading as HTMLElement;
              heading.setAttribute("tabindex", "-1");
              heading.focus();
              heading.addEventListener("blur", () => {
                heading.removeAttribute("tabindex");
              }, { once: true });
            }
          }
        }, 100);
      };

      // Subscribe to router state changes
      const unsubscribe = router.subscribe("onLoad", handleRouteChange);
      
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }, [router]);

    return (
      <div
        className={`bg-bg-brand-secondary-default min-h-screen ${isMySavedPlacesActive || isMapActive ? "h-screen overflow-hidden" : ""}`}
      >
        {!isProduction && (
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
        )}
        {/* Skip to main content link for keyboard users */}
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-container-brand-default focus:text-fg-neutral-strong focus:rounded focus-blue"
        >
          Skip to content
        </a>
        
        {/* Hide navbar on venue photos page */}
        {!isVenuePhotosActive && <Navbar />}
        <Outlet />
        {!isMapActive && (
          <ScrollToTop
            smooth
            aria-label="Scroll to top"
            component={
              <img
                src={
                  isDark
                    ? "/images/BackToTop_Dark.svg"
                    : "/images/BackToTop_Light.svg"
                }
                alt="Scroll to top"
              />
            }
          />
        )}
        <GetInTouchModal />
        <MixpanelCaptureDevPanel />
      </div>
    );
  },
  notFoundComponent: notFoundComponent,
});

function notFoundComponent() {
  return <NotFound />;
}
